import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import Reveal from "./Reveal";

const ease = [0.22, 1, 0.36, 1] as const;

interface Stat {
  prefix?: string;
  value: number;
  decimals?: number;
  suffix?: string;
  caption: string;
  attribution: string;
}

const ROW_ONE: Stat[] = [
  {
    prefix: ">",
    value: 10,
    suffix: "×",
    caption: "modeled ROI potential compared with building equivalent AI infrastructure in-house",
    attribution: "AMC health system",
  },
  {
    value: 10,
    suffix: "+",
    caption: "clinical and operational systems connected through one infrastructure layer",
    attribution: "Regional health system",
  },
  {
    value: 500,
    suffix: "+",
    caption: "hospitals and care sites covered by one AI infrastructure layer",
    attribution: "Nationwide deployment",
  },
  {
    prefix: "≤",
    value: 6,
    suffix: "w",
    caption: "to value realization for new AI workflows, compared with multi-year in-house infrastructure programs",
    attribution: "Multi-site health system",
  },
];

const ROW_TWO: Stat[] = [
  {
    value: 95,
    suffix: "%",
    caption: "less implementation effort for each additional AI workflow once deepc infrastructure is in place",
    attribution: "Enterprise health system benchmark",
  },
  {
    prefix: "~$",
    value: 250,
    suffix: "k",
    caption: "avoided duplicated effort per added AI workflow - procurement, security, validation, deployment, and monitoring",
    attribution: "Multi-site health network",
  },
  {
    prefix: "~$",
    value: 500,
    suffix: "k",
    caption: "annual value potential from one in-house AI workflow deployed through shared infrastructure",
    attribution: "Leading academic medical center",
  },
  {
    prefix: "~$",
    value: 8,
    suffix: "m+",
    caption: "documentation efficiency potential from AI-assisted reporting and clinical documentation workflows",
    attribution: "Regional health network",
  },
];


const Counter = ({
  to,
  decimals = 0,
  duration = 1.8,
  start,
  delay = 0,
}: {
  to: number;
  decimals?: number;
  duration?: number;
  start: boolean;
  delay?: number;
}) => {
  const [val, setVal] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!start) return;
    if (reduce) {
      setVal(to);
      return;
    }
    let raf = 0;
    let t0 = 0;
    const step = (t: number) => {
      if (!t0) t0 = t;
      const elapsed = t - t0 - delay * 1000;
      if (elapsed < 0) {
        raf = requestAnimationFrame(step);
        return;
      }
      const p = Math.min(1, elapsed / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(eased * to);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [start, to, duration, delay, reduce]);

  return <span>{val.toFixed(decimals)}</span>;
};

const StatCard = ({
  stat,
  index,
  inView,
  className = "",
  featured = false,
}: {
  stat: Stat;
  index: number;
  inView: boolean;
  className?: string;
  featured?: boolean;
}) => {
  const decimals = stat.decimals ?? 0;
  const delay = 0.08 + index * 0.07;

  return (
    <motion.article
      initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease, delay }}
      className={`group relative flex flex-col rounded-card border border-border p-card-compact transition-colors hover:border-foreground/30 ${className}`}
      style={{ backgroundColor: "hsl(var(--seabed))" }}
    >
      <div className="relative font-display leading-[0.95] tracking-tight text-primary tabular-nums text-[48px] md:text-[64px]">
        <span className="text-primary/70">{stat.prefix}</span>
        <Counter
          to={stat.value}
          decimals={decimals}
          start={inView}
          delay={delay}
        />
        <span>{stat.suffix}</span>
      </div>

      <p className="relative mt-5 leading-relaxed text-seabed-foreground/80 text-[14px] md:text-[15px]">
        {stat.caption}
      </p>

      <div className="relative mt-auto pt-8 md:pt-10">
        <motion.div
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.9, ease, delay: delay + 0.45 }}
          style={{ transformOrigin: "left" }}
          className="h-px bg-seabed-foreground/15"
          aria-hidden
        />
        <p className="mt-4 font-mono text-[11px] uppercase tracking-mono text-seabed-foreground/55">
          {stat.attribution}
        </p>
      </div>

    </motion.article>
  );
};

const ResultsStrip = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-12% 0px" });

  const all = useMemo(() => [...ROW_ONE, ...ROW_TWO], []);

  return (
    <section
      className="relative py-section-tight border-y border-border overflow-hidden"
      style={{
        ["--background" as any]: "var(--seabed)",
        ["--foreground" as any]: "var(--seabed-foreground)",
        ["--card" as any]: "var(--seabed-elevated)",
        ["--card-foreground" as any]: "var(--seabed-foreground)",
        ["--muted" as any]: "var(--seabed-deep)",
        ["--muted-foreground" as any]: "var(--seabed-muted)",
        ["--border" as any]: "var(--seabed-line)",
        backgroundColor: "hsl(var(--seabed))",
        color: "hsl(var(--seabed-foreground))",
      }}
    >
      {/* Dot grid background */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, hsl(var(--seabed-foreground) / 0.15) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />
      <div className="container relative z-10">
        <Reveal className="max-w-5xl">
          <p className="eyebrow text-seabed-foreground/60">Outcomes from the network</p>
          <h2 className="display mt-6 text-section-title text-seabed-foreground">
            Where clinical AI becomes measurable operating leverage.
          </h2>
          <p className="mt-5 max-w-3xl text-base md:text-lg leading-relaxed text-seabed-foreground/70">
            Shared infrastructure turns every new AI workflow into reusable integration, governance, and financial leverage - instead of another one-off project.
          </p>
        </Reveal>

        <div
          ref={ref}
          className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4"
        >
          {all.map((stat, i) => {
            // Bento windmill across 12 cols: 6+3+3 / 4+4+4 / 6+6
            const spans = [
              "lg:col-span-6",
              "lg:col-span-3",
              "lg:col-span-3",
              "lg:col-span-4",
              "lg:col-span-4",
              "lg:col-span-4",
              "lg:col-span-6",
              "lg:col-span-6",
            ];
            const featured = i === 0 || i === 6 || i === 7;
            return (
              <StatCard
                key={stat.attribution}
                stat={stat}
                index={i}
                inView={inView}
                className={spans[i] ?? "lg:col-span-3"}
                featured={featured}
              />
            );
          })}
        </div>

        <p className="mt-6 max-w-3xl text-xs leading-relaxed text-seabed-foreground/45">
          Representative outcomes from deployed workflows, national network deployments, and deepc value models. Actual impact depends on workflow scope, baseline process, utilization, and local cost assumptions.
        </p>
      </div>
    </section>
  );
};

export default ResultsStrip;
